import { PhotoMarker } from '../types/types';
import { downloadBlob } from './downloadBlob';

/**
 * Load image through backend proxy to bypass CORS.
 * Falls back to direct fetch if proxy returns 403 (e.g. proxy requires staff login).
 */
export async function loadImageAsBlobUrl(originalUrl: string): Promise<string> {
  const proxiedUrl = `/ironview360/proxy-signed-image?url=${encodeURIComponent(originalUrl)}`;
  let response = await fetch(proxiedUrl);
  if (!response.ok) {
    // Proxy may return 403 for non-staff users (@staff_only decorator)
    if (response.status === 403 || response.status === 401) {
      response = await fetch(originalUrl);
    }
    if (!response.ok) throw new Error('Failed to fetch image blob');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Load image as HTMLImageElement
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Downloads the layout with markers overlayed as a PNG
 * Uses backend proxy to bypass CORS issues with signed GCS URLs
 * Supports both image and PDF layouts
 * 
 * @param svgRef Reference to the SVG element containing the rendered layout
 * @param markers Marker data to render on export
 * @param fileName Name for the downloaded file (without extension)
 */
export const downloadLayoutImage = async (
  svgRef: React.RefObject<SVGSVGElement>,
  markers: PhotoMarker[] = [],
  fileName: string = 'layout-with-markers'
): Promise<void> => {
  if (!svgRef.current) {
    throw new Error('SVG reference not available');
  }

  try {
    const svg = svgRef.current;
    const viewBox = svg.viewBox.baseVal;
    
    // Check if this is a PDF layout by looking for a stored canvas reference
    const pdfCanvas = (svg as any).__pdfCanvas as HTMLCanvasElement | undefined;
    const isPdfLayout = !!pdfCanvas;
    
    let width: number;
    let height: number;
    let backgroundContent: HTMLImageElement | HTMLCanvasElement;

    if (isPdfLayout && pdfCanvas) {
      // PDF Layout: Use the canvas reference stored on the SVG element
      backgroundContent = pdfCanvas;
      
      // Get dimensions from the canvas
      width = pdfCanvas.width;
      height = pdfCanvas.height;
    } else {
      // Image Layout: Get image element
      const imageElement = svg.querySelector('image');
      if (!imageElement) {
        throw new Error('No image found in SVG');
      }
      
      const originalImageUrl = imageElement.getAttribute('href') || imageElement.getAttribute('xlink:href');
      if (!originalImageUrl) {
        throw new Error('No image URL found');
      }

      // Get the actual image dimensions (without viewBox padding)
      const imgWidth = imageElement.getAttribute('width');
      const imgHeight = imageElement.getAttribute('height');
      width = imgWidth ? parseFloat(imgWidth) : viewBox.width;
      height = imgHeight ? parseFloat(imgHeight) : viewBox.height;

      // Load background image through proxy
      const blobUrl = await loadImageAsBlobUrl(originalImageUrl);
      backgroundContent = await loadImage(blobUrl);
      URL.revokeObjectURL(blobUrl);
    }

    // Create canvas with 2x resolution for better quality
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.scale(scale, scale);

    // Fill white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    // Draw the background (image or PDF canvas)
    ctx.drawImage(backgroundContent, 0, 0, width, height);

    // Draw all markers directly from data so exports always include every marker
    // and never include unrelated overlays (e.g. area tags).
    markers.forEach((marker) => {
      const markerWidth = marker.width || 30;
      const markerHeight = marker.height || 18;
      const rectX = marker.x - markerWidth / 2;
      const rectY = marker.y - markerHeight / 2;

      ctx.fillStyle = marker.photos.some((photo) => photo.scan_type === 'Finished') ? '#4477ff' : '#ff0000';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.rect(rectX, rectY, markerWidth, markerHeight);
      ctx.fill();
      ctx.stroke();

      const fontSize = marker.fontSize || 12;
      ctx.fillStyle = '#ffffff';
      ctx.font = `600 ${fontSize}px Inter, Helvetica, Arial, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(marker.number), marker.x, marker.y);
    });

    // Convert canvas to blob and trigger download
    canvas.toBlob(blob => {
      if (!blob) {
        throw new Error('Failed to create blob from canvas');
      }
      downloadBlob(blob, `${fileName}.png`);
    }, 'image/png');

  } catch (error) {
    console.error('Error downloading layout image:', error);
    throw error;
  }
};
